import type { CSSProperties, ReactNode } from "react";
import {
  repartirEnLineas,
  type ColorScheme,
} from "./code-viewer-theme";

/**
 * Tokenizer + syntax highlighter para JSON. Distingue keys (strings
 * seguidos de `:`) de strings normales para colorearlos distinto.
 */

export type JsonTokenType =
  | "key"
  | "string"
  | "number"
  | "bool"
  | "null"
  | "punct"
  | "ws";

export interface JsonToken {
  type: JsonTokenType;
  value: string;
}

export function renderJsonEnLineas(
  texto: string,
  scheme: ColorScheme
): ReactNode[][] {
  const tokens = tokenizeJsonCompleto(texto);
  return repartirEnLineas(tokens, (t) => jsonStyle(t, scheme));
}

export function tokenizeJsonCompleto(texto: string): JsonToken[] {
  const tokens: JsonToken[] = [];
  const n = texto.length;
  let i = 0;

  while (i < n) {
    const c = texto[i];

    if (/\s/.test(c)) {
      const start = i;
      while (i < n && /\s/.test(texto[i])) i++;
      tokens.push({ type: "ws", value: texto.slice(start, i) });
      continue;
    }

    if (c === '"') {
      const start = i;
      i++;
      while (i < n && texto[i] !== '"') {
        if (texto[i] === "\\" && i + 1 < n) i += 2;
        else i++;
      }
      if (i < n) i++; // incluye comilla de cierre
      // ¿Es key? (seguida de `:`, ignorando whitespace)
      let j = i;
      while (j < n && /\s/.test(texto[j])) j++;
      const esKey = texto[j] === ":";
      tokens.push({
        type: esKey ? "key" : "string",
        value: texto.slice(start, i),
      });
      continue;
    }

    if (/[-0-9]/.test(c)) {
      const start = i;
      while (i < n && /[-0-9.eE+]/.test(texto[i])) i++;
      tokens.push({ type: "number", value: texto.slice(start, i) });
      continue;
    }

    if (texto.startsWith("true", i) || texto.startsWith("false", i)) {
      const len = texto.startsWith("true", i) ? 4 : 5;
      tokens.push({ type: "bool", value: texto.slice(i, i + len) });
      i += len;
      continue;
    }
    if (texto.startsWith("null", i)) {
      tokens.push({ type: "null", value: "null" });
      i += 4;
      continue;
    }

    tokens.push({ type: "punct", value: c });
    i++;
  }

  return tokens;
}

const JSON_COLORS: Record<ColorScheme, Record<JsonTokenType, string>> = {
  light: {
    key: "#8250df",
    string: "#0f766e",
    number: "#9a3412",
    bool: "#0550ae",
    null: "#6e7781",
    punct: "#57606a",
    ws: "transparent",
  },
  dark: {
    key: "#c4b5fd",
    string: "#86efac",
    number: "#fbbf24",
    bool: "#7dd3fc",
    null: "#64748b",
    punct: "#94a3b8",
    ws: "transparent",
  },
};

function jsonStyle(type: JsonTokenType, scheme: ColorScheme): CSSProperties {
  const palette = JSON_COLORS[scheme];
  switch (type) {
    case "key":
      return { color: palette.key };
    case "string":
      return { color: palette.string };
    case "number":
      return { color: palette.number };
    case "bool":
      return { color: palette.bool, fontWeight: 500 };
    case "null":
      return { color: palette.null, fontStyle: "italic" };
    case "punct":
      return { color: palette.punct };
    default:
      return {};
  }
}

export function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
